import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { paymentsAPI } from '../lib/api';
import { formatStripeError } from '../lib/stripeErrors';
import { InAppBrowserWarning } from '../components/InAppBrowserWarning';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { CreditCard, Shield, CheckCircle, ArrowLeft, Lock } from 'lucide-react';
import Navbar from '../components/Navbar';

// Initialize Stripe
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51Suz7mBND4x5eqTY4lK8jJnCzQu1eZ5nDVe0LK8TmvKdqN0DVQjfEJBEgLaIy9rGLH7VGKJqRVoZPn7nYUHtjEVH00xzP2BXKA');

const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#ffffff',
      '::placeholder': {
        color: '#6b7280',
      },
      iconColor: '#ff007f',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
  },
};

const AddCardForm = ({ onSuccess, returnUrl }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [clientSecret, setClientSecret] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    initializeSetupIntent();
  }, []);

  const initializeSetupIntent = async () => {
    try {
      const res = await paymentsAPI.saveCard(window.location.origin);
      setClientSecret(res.data.client_secret);
    } catch (err) {
      console.error('Failed to initialize card setup:', err);
      setError('Failed to initialize card setup. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const cardElement = elements.getElement(CardElement);
      
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement,
        },
      });

      if (stripeError) {
        setError(formatStripeError(stripeError));
        setLoading(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        // Confirm card saved in backend
        await paymentsAPI.confirmCardSaved();
        toast.success('Card saved successfully!');
        onSuccess();
      }
    } catch (err) {
      console.error('Card setup error:', err);
      setError(err.response?.data?.detail || 'Failed to save card. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <InAppBrowserWarning />
      <div className="p-4 bg-[#1a1a1a] rounded-lg border border-[#333]">
        <CardElement options={cardElementOptions} />
      </div>

      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex items-center gap-2 text-white/50 text-sm">
        <Lock className="w-4 h-4" />
        <span>Your card details are securely processed by Stripe</span>
      </div>

      <Button
        type="submit"
        disabled={!stripe || !clientSecret || loading}
        className="w-full bg-primary hover:bg-primary/90 h-12"
        data-testid="save-card-btn"
      >
        {loading ? (
          <>
            <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-2" />
            Saving Card...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5 mr-2" />
            Save Card
          </>
        )}
      </Button>
    </form>
  );
};

const AddCardPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user } = useAuth();
  const [savedCard, setSavedCard] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const returnUrl = searchParams.get('return') || '/classes';
  const classId = searchParams.get('classId');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login?return=' + encodeURIComponent(window.location.pathname + window.location.search));
      return;
    }
    fetchSavedCard();
  }, [isAuthenticated]);

  const fetchSavedCard = async () => {
    try {
      const res = await paymentsAPI.getMySavedCard();
      setSavedCard(res.data);
    } catch (error) {
      console.error('Failed to fetch saved card:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    fetchSavedCard();
    if (classId) {
      navigate(`/classes/${classId}`);
    } else {
      navigate(returnUrl);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a]">
        <Navbar />
        <div className="container mx-auto px-4 py-20 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <Navbar />
      <div className="container mx-auto px-4 py-12 max-w-lg">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-white/60 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card className="bg-[#111] border-[#333]">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CreditCard className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-white text-2xl">
              {savedCard?.has_card ? 'Update Payment Card' : 'Add Payment Card'}
            </CardTitle>
            <p className="text-white/60 mt-2">
              Save your card securely for class bookings. You won't be charged until your booking is authorized.
            </p>
          </CardHeader>
          <CardContent className="space-y-6">
            {savedCard?.has_card && (
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <div>
                    <p className="text-green-400 font-medium">Card on File</p>
                    <p className="text-white/60 text-sm">
                      {savedCard.brand?.toUpperCase()} •••• {savedCard.last4} (expires {savedCard.exp})
                    </p>
                  </div>
                </div>
              </div>
            )}

            <Elements stripe={stripePromise}>
              <AddCardForm onSuccess={handleSuccess} returnUrl={returnUrl} />
            </Elements>

            {/* Security badges */}
            <div className="flex items-center justify-center gap-4 pt-4 border-t border-[#333]">
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Shield className="w-4 h-4" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-2 text-white/40 text-xs">
                <Lock className="w-4 h-4" />
                <span>PCI Compliant</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info section */}
        <Card className="bg-[#111] border-[#333] mt-6">
          <CardContent className="p-4">
            <h4 className="text-white font-medium mb-2">How it works</h4>
            <ul className="text-white/60 text-sm space-y-2">
              <li className="flex items-start gap-2">
                <span className="text-primary">1.</span>
                Add your card details securely
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">2.</span>
                Book classes for your children
              </li>
              <li className="flex items-start gap-2">
                <span className="text-primary">3.</span>
                Payment is only taken when authorized by the school
              </li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AddCardPage;
